import React, { useEffect, useRef, useState, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Alert,
  Skeleton,
  Chip,
  Paper
} from '@mui/material';
import { getBusinessCoordinatesWithFallback, logCoordinateDebug } from '../../utils/coordinateUtils';

const ComparisonMap = ({
  keywordData,
  businessLocation,
  title = "Map",
  mapId,
  baselineData = null, // Für Vergleichsmarker
  Inpdf = false,
  comparisonData = null
}) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [mapError, setMapError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true);
  const markersRef = useRef([]);

  // Helper function für Datum-Validierung
  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date);
  };

  // Initialisiere Karte nur einmal
  useEffect(() => {
    let mounted = true;
    
    const initMap = async () => {
      if (!mounted) return;
      
      try {
        setMapLoading(true);
        
        // Improved map cleanup to prevent container reuse errors
        if (map) {
          try {
            // Clear all layers first
            map.eachLayer(layer => {
              map.removeLayer(layer);
            });
            map.remove();
          } catch (e) {
            console.warn('Error removing map:', e);
          }
          setMap(null);
        }

        // Thoroughly clear the map container
        if (mapRef.current) {
          try {
            // Remove any existing Leaflet instance data
            if (mapRef.current._leaflet_id) {
              delete mapRef.current._leaflet_id;
            }
            mapRef.current.innerHTML = '';
            // Force garbage collection hint
            mapRef.current.style.display = 'none';
            // eslint-disable-next-line no-unused-expressions
            mapRef.current.offsetHeight; // Trigger reflow
            mapRef.current.style.display = '';
          } catch (e) {
            console.warn('Error cleaning map container:', e);
          }
        }

        // Longer delay for thorough cleanup
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!mounted) return;

        // Initialize map
        if (typeof window !== 'undefined' && window.L) {
          initializeMap();
        } else {
          loadLeaflet();
        }
      } catch (error) {
        console.error('Error initializing map:', error);
        if (mounted) {
          setMapError(true);
          setMapLoading(false);
        }
      }
    };

    initMap();

    return () => {
      mounted = false;
      if (map) {
        try {
          map.remove();
        } catch (e) {
          console.warn('Error removing map in cleanup:', e);
        }
      }
      if (mapRef.current) {
        mapRef.current.innerHTML = '';
        mapRef.current._leaflet_id = null;
      }
    };
  }, [mapId, keywordData]); // Re-init map when keywordData changes

  // Marker-Updates bei Datenänderung
  useEffect(() => {
    if (map && keywordData) {
      // Add small delay to ensure map is fully rendered
      const timer = setTimeout(() => {
        updateMapMarkers();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [keywordData, baselineData, comparisonData, map, Inpdf]);

  const loadLeaflet = () => {
    // CSS laden
    if (!document.querySelector('link[href*="leaflet.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }

    // JavaScript laden
    if (!window.L) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initializeMap;
      script.onerror = () => setMapError(true);
      document.head.appendChild(script);
    }
  };

  const initializeMap = useCallback(() => {
    try {
      if (!mapRef.current || !window.L) return;

      // Check if container already has a map
      if (mapRef.current._leaflet_id) {
        console.warn('Map container already initialized, clearing...');
        mapRef.current.innerHTML = '';
        mapRef.current._leaflet_id = null;
      }

      // Double-check the container is empty
      if (mapRef.current.children.length > 0) {
        mapRef.current.innerHTML = '';
      }

      // Get business coordinates - fallback to test location center if business doesn't have coordinates
      const keywords = keywordData ? [keywordData] : [];

      console.log('🗺️ COMPARISON MAP: Map initialization', {
        keywordDataPresent: !!keywordData,
        testLocationsLength: keywordData?.testLocations?.length || 0,
        businessLocationPresent: !!businessLocation,
        mapId
      });

      let businessLat, businessLng;

      try {
        const coordinates = getBusinessCoordinatesWithFallback(businessLocation, keywords);
        businessLat = coordinates.lat;
        businessLng = coordinates.lng;
      } catch (coordError) {
        // Fallback: Calculate center from test locations if business coordinates unavailable
        if (keywordData?.testLocations && keywordData.testLocations.length > 0) {
          const validLocations = keywordData.testLocations.filter(
            loc => loc.lat && loc.lng && !isNaN(loc.lat) && !isNaN(loc.lng)
          );

          if (validLocations.length > 0) {
            const avgLat = validLocations.reduce((sum, loc) => sum + parseFloat(loc.lat), 0) / validLocations.length;
            const avgLng = validLocations.reduce((sum, loc) => sum + parseFloat(loc.lng), 0) / validLocations.length;
            businessLat = avgLat;
            businessLng = avgLng;
            console.log('🗺️ COMPARISON MAP: Using center of test locations:', { businessLat, businessLng });
          } else {
            throw new Error('No valid coordinates found in test locations');
          }
        } else {
          throw coordError;
        }
      }

      // Log coordinate usage
      logCoordinateDebug(businessLocation, keywords, 'ComparisonMap');

      const mapInstance = window.L.map(mapRef.current, {
        zoomControl: true,
        attributionControl: true,
        preferCanvas: true // Bessere Performance für viele Marker
      }).setView([businessLat, businessLng], 10); // Zoom 12 for better detail

      // OpenStreetMap Tiles mit optimierten Einstellungen
      window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 18,
        tileSize: 256,
        zoomOffset: 0
      }).addTo(mapInstance);

      setMap(mapInstance);
      setMapLoading(false);

      // Ensure map renders properly before fitting bounds
      mapInstance.invalidateSize(false);

    } catch (error) {
      console.error('Error initializing map:', error);
      setMapError(true);
      setMapLoading(false);
    }
  }, [businessLocation?.lat, businessLocation?.lng, keywordData?.testLocations]);

  const createRankMarker = (rank, hasChange = false, changeType = null, isNoProgressMarker = false, noProgressColor = null, isGreyMarker = false) => {
    // For PDF mode markers with no/negative progress, show marker without number
    if (isNoProgressMarker) {
      // For PDF mode, always use grey. Otherwise use the provided color
      const backgroundColor = isGreyMarker ? 'rgba(158, 158, 158, 0.55)' : (noProgressColor || '#2196f3');
      const borderStyle = '3px solid white';

      return window.L.divIcon({
        html: `
          <div style="
            background: ${backgroundColor};
            border: ${borderStyle};
            border-radius: 50%;
            width: 46px;
            height: 46px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 6px rgba(0,0,0,0.15);
            font-weight: bold;
            color: white;
            font-size: 18px;
            cursor: pointer;
            position: relative;
          ">
          </div>
        `,
        className: 'custom-rank-marker',
        iconSize: [46, 46],
        iconAnchor: [23, 23]
      });
    }

    // FIXED: Handle N/A, null, undefined as worst ranking (20)
    const effectiveRank = rank && rank !== 'N/A' && !isNaN(rank) ? rank : 20;
    const displayRank = rank && rank !== 'N/A' && !isNaN(rank) ? rank : '20';

    let backgroundColor = '#f44336'; // Rot für schlechte Rankings (Default)

    if (effectiveRank <= 3) backgroundColor = '#4caf50'; // Grün für Top 3
    else if (effectiveRank <= 10) backgroundColor = '#ff9800'; // Orange für Top 10
    // Alles andere bleibt rot (#f44336)

    // Marker mit optionalem Vergleichsindikator
    let borderStyle = '3px solid white';
    if (hasChange) {
      if (changeType === 'improvement') borderStyle = '4px solid #4caf50';
      else if (changeType === 'decline') borderStyle = '4px solid #f44336';
      else if (changeType === 'new') borderStyle = '4px dashed #2196f3';
    }

    return window.L.divIcon({
      html: `
        <div style="
          background: ${backgroundColor};
          border: ${borderStyle};
          border-radius: 50%;
          width: 46px;
          height: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 6px rgba(0,0,0,0.15);
          font-weight: bold;
          color: white;
          font-size: 18px;
          cursor: pointer;
          position: relative;
        ">
          ${displayRank}
          ${hasChange ? `<div style="
            position: absolute;
            top: -10px;
            right: -10px;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: ${changeType === 'improvement' ? '#4caf50' : changeType === 'decline' ? '#f44336' : '#2196f3'};
            border: 3px solid white;
            font-size: 14px;
            font-weight: bold;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          ">${changeType === 'improvement' ? '↗' : changeType === 'decline' ? '↘' : '★'}</div>` : ''}
        </div>
      `,
      className: 'custom-rank-marker',
      iconSize: [46, 46],
      iconAnchor: [23, 23]
    });
  };



  // Optimierte Marker-Updates
  const updateMapMarkers = useCallback(() => {
    if (!map || !window.L || !keywordData) return;

    // Log PDF mode for debugging
    if (Inpdf) {
      console.log('🗺️ PDF MODE: Rendering markers', {
        mapId,
        locationCount: keywordData.testLocations?.length || 0,
        hasBaselineData: !!baselineData,
        hasComparisonData: !!comparisonData
      });
    }

    // Entferne vorherige Marker
    markersRef.current.forEach(marker => {
      try {
        map.removeLayer(marker);
      } catch (e) {
        // Marker bereits entfernt
      }
    });
    markersRef.current = [];

    const testLocations = keywordData.testLocations || [];
    
    if (testLocations.length === 0) {
      // Fallback: Create a single marker at the business location when no test data is available
      const keywords = keywordData ? [keywordData] : [];
      const coordinates = getBusinessCoordinatesWithFallback(businessLocation, keywords);
      
      const businessMarker = window.L.marker([coordinates.lat, coordinates.lng], {
        icon: createRankMarker('N/A', false, null),
        riseOnHover: true
      });

      businessMarker.bindPopup(`
        <div style="max-width: 280px; font-family: Arial, sans-serif;">
          <h4 style="margin: 0 0 8px 0; color: #333;">${businessLocation?.name || 'Ihr Unternehmen'}</h4>
          <p style="color: #1976d2; font-weight: bold; margin: 4px 0;">🏢 Ihr Firmenstandort</p>
          
          <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; margin: 8px 0;">
            <strong>Status:</strong> 
            <span style="color: #666; font-weight: normal;">
              Keine Ranking-Daten verfügbar
            </span>
          </div>
          
          <p style="margin: 8px 0 4px 0; font-size: 0.9em; color: #666;">
            Führen Sie eine Messung durch, um Ranking-Daten zu sehen.
          </p>
        </div>
      `, {
        maxWidth: 300,
        className: 'custom-popup'
      });

      try {
        businessMarker.addTo(map);
        markersRef.current = [businessMarker];
        
        // Center map on business location
        map.setView([coordinates.lat, coordinates.lng], 12);
      } catch (error) {
        console.error('Error adding business marker to map:', error);
        console.error('Map state:', { mapExists: !!map, businessMarkerExists: !!businessMarker });
      }
      return;
    }

    // Füge Ranking-Marker hinzu
    const newMarkers = [];

    testLocations.forEach((location, index) => {
      if (location.lat && location.lng) {
        // Prüfe auf Veränderungen wenn baselineData vorhanden
        let hasChange = false;
        let changeType = null;
        let baselineRank = null;
        let shouldShowAsBlueMarker = false; // For PDF mode

        if (baselineData && baselineData.testLocations) {
          // Try to find matching baseline location: first by index (PDF mode), then by name/coordinates
          let baselineLocation = baselineData.testLocations[index];

          if (!baselineLocation ||
              (baselineLocation.lat && Math.abs(parseFloat(baselineLocation.lat) - parseFloat(location.lat)) > 0.01) ||
              (baselineLocation.lng && Math.abs(parseFloat(baselineLocation.lng) - parseFloat(location.lng)) > 0.01)) {
            // If index doesn't match or coordinates are too different, search by name/coordinates
            baselineLocation = baselineData.testLocations.find(loc =>
              (loc.locationName == location.locationName) ||  (Math.abs(loc.lat - location.lat) < 0.01 && Math.abs(loc.lng - location.lng) < 0.01)
            );
          }

          if (baselineLocation) {
            // CORRECTED: Proper handling of rankings for comparison
            const isBaselineFound = baselineLocation.rank && baselineLocation.rank !== 'N/A' && !isNaN(baselineLocation.rank);
            const isCurrentFound = location.rank && location.rank !== 'N/A' && !isNaN(location.rank);

            // Store actual ranks (not effective ranks)
            baselineRank = isBaselineFound ? baselineLocation.rank : null;
            const currentRank = isCurrentFound ? location.rank : null;

            // Calculate change only when both rankings exist
            if (isBaselineFound && isCurrentFound) {
              const rankDiff = baselineLocation.rank - location.rank;

              if (Inpdf && index < 3) {
                console.log(`  [comparison map] index=${index}: baseline=${baselineLocation.rank} vs current=${location.rank}, diff=${rankDiff}`);
              }

              if (rankDiff > 0) {
                hasChange = true;
                changeType = 'improvement';
              } else if (rankDiff < 0) {
                hasChange = true;
                changeType = 'decline';
                shouldShowAsBlueMarker = Inpdf; // Negative progress = blue
              } else {
                // rankDiff === 0, no change/no progress
                changeType = null;
                hasChange = false;
                shouldShowAsBlueMarker = Inpdf; // No progress = grey marker in PDF mode
              }
            } else if (isBaselineFound && !isCurrentFound) {
              // Was found, now not found
              hasChange = true;
              changeType = 'decline';
              shouldShowAsBlueMarker = Inpdf;
            } else if (!isBaselineFound && isCurrentFound) {
              // Was not found, now found
              hasChange = true;
              changeType = 'improvement';
            } else {
              // Both not found = no change
              shouldShowAsBlueMarker = Inpdf;
            }
          } else {
            hasChange = true;
            changeType = 'new';
          }
        }

        // For baseline map in PDF mode with comparison data
        if (!baselineData && comparisonData && comparisonData.testLocations && Inpdf) {
          // Try to find matching comparison location: first by index (PDF mode), then by name/coordinates
          let comparisonLocation = comparisonData.testLocations[index];

          if (!comparisonLocation ||
              (comparisonLocation.lat && Math.abs(parseFloat(comparisonLocation.lat) - parseFloat(location.lat)) > 0.01) ||
              (comparisonLocation.lng && Math.abs(parseFloat(comparisonLocation.lng) - parseFloat(location.lng)) > 0.01)) {
            // If index doesn't match or coordinates are too different, search by name/coordinates
            comparisonLocation = comparisonData.testLocations.find(loc =>
              (loc.locationName == location.locationName) ||  (Math.abs(loc.lat - location.lat) < 0.01 && Math.abs(loc.lng - location.lng) < 0.01)
            );
          }

          if (comparisonLocation) {
            const isBaselineFound = location.rank && location.rank !== 'N/A' && !isNaN(location.rank);
            const isComparisonFound = comparisonLocation.rank && comparisonLocation.rank !== 'N/A' && !isNaN(comparisonLocation.rank);

            if (isBaselineFound && isComparisonFound) {
              const rankDiff = location.rank - comparisonLocation.rank;

              if (Inpdf && index < 3) {
                console.log(`  [baseline map] index=${index}: baseline=${location.rank} vs comparison=${comparisonLocation.rank}, diff=${rankDiff}`);
              }

              if (rankDiff > 0) {
                hasChange = true;
                changeType = 'improvement';
                shouldShowAsBlueMarker = false;
              } else if (rankDiff < 0) {
                hasChange = true;
                changeType = 'decline';
                shouldShowAsBlueMarker = true;
              } else {
                // rankDiff === 0, no change/no progress
                changeType = null;
                hasChange = false;
                shouldShowAsBlueMarker = Inpdf; // No progress = grey marker in PDF mode
              }
            } else if (isBaselineFound && !isComparisonFound) {
              hasChange = true;
              changeType = 'decline';
              shouldShowAsBlueMarker = true;
            } else if (!isBaselineFound && isComparisonFound) {
              hasChange = true;
              changeType = 'improvement';
              shouldShowAsBlueMarker = false;
            } else {
              // Both not found = no change
              shouldShowAsBlueMarker = true;
            }
          } else {
            // Location not found in comparison
            shouldShowAsBlueMarker = true;
          }
        }

        // Apply blue marker logic in PDF mode for both maps
        // Show blue markers for locations with no/negative progress
        const isNoProgressMarker = Inpdf ? shouldShowAsBlueMarker : false;

        if (Inpdf && index < 3) {
          console.log(`🗺️ MARKER[${mapId}] index=${index}: rank=${location.rank}, changeType=${changeType}, isBlue=${isNoProgressMarker}`);
        }

        // For baseline map in PDF mode, don't show progress indicators
        const isBaselineMapPdf = baselineData === null && Inpdf;
        const displayChangeType = isBaselineMapPdf ? null : changeType;
        const displayHasChange = isBaselineMapPdf ? false : hasChange;
        // Use grey for no-progress markers on both maps in PDF mode
        const isGreyMarker = Inpdf && isNoProgressMarker;

        const marker = window.L.marker([location.lat, location.lng], {
          icon: createRankMarker(location.rank, displayHasChange, displayChangeType, isNoProgressMarker, null, isGreyMarker),
          riseOnHover: true
        });

        // Popup mit Vergleichsinformationen
        const isCenter = location.isCenter || location.locationName === 'Firmenstandort';
        const popupTitle = isCenter ? 
          `${businessLocation?.name || 'Ihr Unternehmen'} (Firmenstandort)` : 
          location.locationName;
        
        const competitorsList = location.competitors?.slice(0, 5).map(comp => 
          `<li style="margin: 2px 0;">${comp.name} <span style="color: #666;">(Rang ${comp.rank})</span></li>`
        ).join('') || '<li>Keine Wettbewerber-Daten</li>';

        // Vergleichsinformationen für Popup
        let comparisonInfo = '';
        if (baselineData && hasChange) {
          // Use actual rankings for display and calculation
          const isCurrentFound = location.rank && location.rank !== 'N/A' && !isNaN(location.rank);
          const isBaselineFound = baselineRank !== null;
          
          const baselineDisplay = isBaselineFound ? baselineRank : 'Nicht gefunden';
          const currentDisplay = isCurrentFound ? location.rank : 'Nicht gefunden';
          
          let changeText = '';
          if (isBaselineFound && isCurrentFound) {
            const rankChange = baselineRank - location.rank;
            if (rankChange > 0) {
              changeText = `📈 Verbessert um ${rankChange} Plätze (${baselineDisplay} → ${currentDisplay})`;
            } else {
              changeText = `📉 Verschlechtert um ${Math.abs(rankChange)} Plätze (${baselineDisplay} → ${currentDisplay})`;
            }
          } else if (isBaselineFound && !isCurrentFound) {
            changeText = `📉 Nicht mehr gefunden (${baselineDisplay} → ${currentDisplay})`;
          } else if (!isBaselineFound && isCurrentFound) {
            changeText = `📈 Neu gefunden (${baselineDisplay} → ${currentDisplay})`;
          }
          
          comparisonInfo = `
            <div style="background: ${changeType === 'improvement' ? '#e8f5e8' : '#ffeaea'}; padding: 8px; border-radius: 4px; margin: 8px 0;">
              <strong>${changeText}</strong>
            </div>
          `;
        } else if (baselineData && !hasChange) {
          const isCurrentFound = location.rank && location.rank !== 'N/A' && !isNaN(location.rank);
          const currentDisplay = isCurrentFound ? location.rank : 'Nicht gefunden';
          comparisonInfo = `
            <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; margin: 8px 0;">
              <strong>📊 Unverändert (${currentDisplay})</strong>
            </div>
          `;
        }

        marker.bindPopup(`
          <div style="max-width: 280px; font-family: Arial, sans-serif;">
            <h4 style="margin: 0 0 8px 0; color: #333;">${popupTitle}</h4>
            ${isCenter ? '<p style="color: #1976d2; font-weight: bold; margin: 4px 0;">🏢 Ihr Firmenstandort</p>' : ''}
            
            <div style="background: #f5f5f5; padding: 8px; border-radius: 4px; margin: 8px 0;">
              <strong>Aktueller Rang:</strong>
              <span style="color: ${(() => {
                const effectiveRank = location.rank && location.rank !== 'N/A' && !isNaN(location.rank) ? location.rank : 20;
                return effectiveRank <= 3 ? '#4caf50' : effectiveRank <= 10 ? '#ff9800' : '#f44336';
              })()}; font-weight: bold;">
                ${location.rank && location.rank !== 'N/A' && !isNaN(location.rank) ? location.rank : 'Nicht gefunden (20)'}
              </span>
            </div>
            
            ${comparisonInfo}
            
            <p style="margin: 8px 0 4px 0; font-weight: bold; color: #555;">Top Wettbewerber:</p>
            <ul style="margin: 4px 0; padding-left: 16px; font-size: 0.9em;">
              ${competitorsList}
            </ul>
          </div>
        `, {
          maxWidth: 300,
          className: 'custom-popup'
        });

        try {
          marker.addTo(map);
          newMarkers.push(marker);
        } catch (error) {
          console.error('Error adding marker to map:', error);
          console.error('Map state:', { mapExists: !!map, markerExists: !!marker });
        }
      }
    });

    markersRef.current = newMarkers;

    // Optimierte Zoom-Anpassung nur bei Bedarf
    if (newMarkers.length > 0) {
      try {
        // Critical: Invalidate map size first to ensure proper bounds calculation
        // This forces Leaflet to recalculate based on actual container dimensions
        setTimeout(() => {
          if (map && mapRef.current) {
            map.invalidateSize(false);

            const group = new window.L.featureGroup(newMarkers);
            const bounds = group.getBounds();

            // Validate bounds - check if they are valid and have area
            if (bounds.isValid && bounds.isValid()) {
              // Check if bounds have actual area (not all points at same location)
              const hasArea = !bounds.getNorthEast().equals(bounds.getSouthWest());

              if (hasArea) {
                // Add padding for better visibility of all markers
                // maxZoom capped at 11 to prevent over-zooming when points are close together
                map.fitBounds(bounds.pad(0.1), { maxZoom: 11, animate: true });
                console.log('✅ Map bounds fitted successfully');
              } else {
                // All markers at same point - zoom to that point instead
                console.warn('⚠️ All markers at same location, zooming to center');
                map.setView(bounds.getCenter(), 13);
              }
            } else {
              console.warn('⚠️ Invalid bounds detected, using default zoom');
              map.setZoom(13);
            }
          }
        }, 100); // Small delay to ensure container is rendered
      } catch (boundsError) {
        console.error('❌ Error fitting map bounds:', boundsError.message);
        // Fallback: center on business location
        if (businessLocation?.lat && businessLocation?.lng) {
          map.setView([parseFloat(businessLocation.lat), parseFloat(businessLocation.lng)], 13);
        }
      }
    }
  }, [map, keywordData, businessLocation, baselineData, comparisonData, Inpdf]);

  if (mapError) {
    return (
      <Alert severity="error">
        Karte konnte nicht geladen werden. Prüfen Sie Ihre Internetverbindung.
      </Alert>
    );
  }

  if (!keywordData) {
    return (
      <Alert severity="info">
        Keine Daten für diese Periode verfügbar.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      {/* Map Container */}
      <Box
        sx={{
          height: '100%',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {mapLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <Box textAlign="center">
              <Skeleton variant="rectangular" width="100%" height={60} sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary">
                {title} wird geladen...
              </Typography>
            </Box>
          </Box>
        )}
        
        <Box
          ref={mapRef}
          sx={{
            height: '100%',
            width: '100%',
            '& .leaflet-popup-content-wrapper': {
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            },
            '& .custom-popup .leaflet-popup-content': {
              margin: '8px',
              lineHeight: '1.4'
            }
          }}
        />
      </Box>

      {/* Map Info Overlay */}
      <Paper
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          px: 1.5,
          py: 0.5,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(8px)',
          zIndex: 1000
        }}
      >
        <Typography variant="caption" fontWeight={600} color="primary">
          📍 {keywordData.testLocations?.length || 0} Messpunkte
        </Typography>
        {keywordData.averageRank !== undefined && keywordData.averageRank !== null && !isNaN(keywordData.averageRank) && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            • Ø {keywordData.averageRank.toFixed(1)}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default ComparisonMap;